import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Contexts
import { BundleContext } from "../../context/BundleProvider";
import { AuthContext } from "../../context/AuthProvider";
import { ThemeContext } from "../../context/ThemeProvider";
import { useAnalytics } from "../../context/AnalyticsProvider";

// Components
import {
  TestListGrid,
  PracticeTestDialog,
  EmptyTestState
} from "./components";
import CommonHeader from "../../components/common/CommonHeader";
import StatsGrid from "../../components/common/StatsGrid";
import { faClipboardCheck, faCheckCircle, faChartLine, faTrophy, faQuestionCircle, faPercentage,faClipboardList } from "@fortawesome/free-solid-svg-icons";
import PremiumDialog from "../../components/common/PremiumDialog";

// Constants
import { GET_PRACTICE_TEST } from "../../../app_constant";
import { STRINGS } from "../../constant";

let _practiceTestTracked = false;

const PracticeTest = () => {
  const navigate = useNavigate();
  const { isSubscribe, profile, questionsList, community } = useContext(AuthContext);
  const { selectedCertificate } = useContext(BundleContext);
  const { theme } = useContext(ThemeContext);
  const { track, trackTestStart } = useAnalytics();

  // State management
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [showPremiumDialog, setShowPremiumDialog] = useState(false);
  const PRACTICE_TEST = GET_PRACTICE_TEST(selectedCertificate);

  // Track practice test list view (once per page load)
  useEffect(() => {
    if (_practiceTestTracked) return;
    _practiceTestTracked = true;
    track('practice_test_menu_viewed', {
      tests_available: PRACTICE_TEST?.length || 0,
      is_premium: isSubscribe
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate statistics
  const calculateStats = () => {
    const testHistory = profile?.practice_test_history || [];
    
    // Total tests available
    const totalTests = PRACTICE_TEST?.length || 0;
    
    // Tests completed
    const completedTests = new Set(testHistory.map(h => h.test_name)).size;
    
    // Average score across all tests
    const totalScore = testHistory.reduce((sum, test) => sum + (test.score || 0), 0);
    const avgScore = testHistory.length > 0 ? Math.round(totalScore / testHistory.length) : 0;
    
    // Best score
    const bestScore = testHistory.length > 0 
      ? Math.max(...testHistory.map(h => h.score || 0))
      : 0;
    
    // Total questions across all tests
    const totalQuestions = PRACTICE_TEST.reduce((sum, test) => {
      const questions = questionsList?.filter(q => q.practice_test_id === test.practice_test_id) || [];
      return sum + questions.length;
    }, 0);

    const statsData = [
      {
        icon: faClipboardList,
        stat: totalTests,
        label: 'Available Tests',
        color: theme?.primary?.orange?.[500],
        iconType: 'fontawesome'
      },
      {
        icon: faCheckCircle,
        stat: completedTests,
        label: 'Tests Completed',
        color: theme?.primary?.orange?.[500],
        iconType: 'fontawesome'
      },
      {
        icon: faChartLine,
        stat: `${avgScore}%`,
        label: 'Average Score',
        color: theme?.primary?.orange?.[500],
        iconType: 'fontawesome'
      },
      {
        icon: faTrophy,
        stat: `${bestScore}%`,
        label: 'Best Score',
        color: theme?.primary?.orange?.[500],
        iconType: 'fontawesome'
      },
      /* {
        icon: faQuestionCircle,
        stat: totalQuestions.toLocaleString(),
        label: 'Total Questions',
        color: theme?.primary?.orange?.[500],
        iconType: 'fontawesome'
      },
      {
        icon: faPercentage,
        stat: `${totalTests > 0 ? Math.round((completedTests / totalTests) * 100) : 0}%`,
        label: 'Completion Rate',
        color: theme?.primary?.orange?.[500],
        iconType: 'fontawesome'
      }, */
    ];

    return statsData;
  };

  const stats = calculateStats();

  // Handle test selection
  const handleTestClick = (test) => {
    const testQuestions = questionsList?.filter(
      q => q.practice_test_id === test.practice_test_id
    ) || [];
    
    const questionTime = profile?.time_per_question || 
                        community?.total_time_per_question || 
                        90;

    const enhancedTest = {
      ...test,
      test_time: questionTime * testQuestions.length,
      is_custom_test: false,
      num_questions: testQuestions.length,
      questions: testQuestions
    };

    setSelectedTest(enhancedTest);
    setIsDialogOpen(true);
  };

  // Handle dialog actions
  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setSelectedTest(null);
  };

  const handleTestStart = async () => {
    localStorage.setItem("testStart", JSON.stringify(true));
    
    // Track test start
    await trackTestStart('practice', selectedTest?.id || selectedTest?.practice_test_id, {
      test_name: selectedTest?.name || selectedTest?.title,
      question_count: selectedTest?.questions?.length || 0,
      domains: selectedTest?.domains || []
    });
    
    setIsDialogOpen(false);
    navigate("/test", { state: selectedTest });
  };

  const handleChoosePlan = () => {
    setIsDialogOpen(false);
    navigate("/chooseplan");
  };
  const handleUpgrade = () => {
    navigate('/chooseplan');
  };
  
  const handleLockedItemClick = () => {
    setShowPremiumDialog(true);
  };

  return (
    <div className="min-h-screen transition-colors duration-300" 
         style={{ background: theme?.background?.main }}>
      <div>
        {/* Header */}
        
        <CommonHeader
      title="Practice Test"
      icon={faClipboardCheck}
      subtitle="Simulate the real exam experience with timed practice tests"
      quote="Practice makes perfect. Practice with purpose makes excellence."
      onBack={() => navigate("/home")}
      theme={theme}
      showBackButton={true}
    />
        
        {/* Statistics */}
        <StatsGrid 
          stats={stats}
          theme={theme}
        />
        
        {/* Test List */}
        {PRACTICE_TEST && PRACTICE_TEST.length > 0 ? (
          <TestListGrid
            tests={PRACTICE_TEST}
            questionsList={questionsList}
            profile={profile}
            isSubscribe={isSubscribe}
            onTestClick={handleTestClick}
            theme={theme}
            onLockedItemClick={handleLockedItemClick}
          />
        ) : (
          <EmptyTestState theme={theme} />
        )}
        
        {/* Practice Test Dialog */}
        {selectedTest && (
          <PracticeTestDialog
            isOpen={isDialogOpen}
            onClose={handleDialogClose}
            onStart={handleTestStart}
            onChoosePlan={handleChoosePlan}
            testDetails={selectedTest}
            isSubscribe={isSubscribe}
            theme={theme}
          />
        )}
      </div>
      <PremiumDialog
        isOpen={showPremiumDialog}
        onClose={() => setShowPremiumDialog(false)}
        onUpgrade={() => {
          setShowPremiumDialog(false);
          handleUpgrade();
        }}
        theme={theme}
      />
    </div>
  );
};

export default PracticeTest;